<script>
  import { onMount, onDestroy } from "svelte";
  import { fade } from 'svelte/transition';
  import Button from "tttl-core-ui/src/components/Button.svelte";
  import { Label } from "@smui/data-table/styled";
  import Textfield from "@smui/textfield";
  import HelperText from '@smui/textfield/helper-text';
  import Select, {Option} from '@smui/select';
  import Checkbox from '@smui/checkbox';
  import FormField from '@smui/form-field';
  import ToastSuccess from 'src/building-blocks/toast-success.svelte';
  import ToastError from 'src/building-blocks/toast-error.svelte';
  import { toastOptions, toastSuccessTheme, toastErrorTheme } from 'src/building-blocks/toast-constants.js';
  import {SvelteToast, toast} from "@zerodevx/svelte-toast";
  import CircularProgress from '@smui/circular-progress';
  import ConfirmSurveyConfigurationModal from './confirm-survey-configuration-modal.svelte';
  import * as surveyApi from "src/services/integrationsEngineApi/surveyConfiguration"
  import * as v3Api from "src/services/integrationsEngineApi/v3"
  import * as smsApi from "src/services/smsApi/sms"
  import * as merchantsApi from "src/services/api/merchants"

  import * as channelsApi from "src/services/api/channels"
  import * as questionnairesApi from "src/services/api/questionnaires"
  import { PreferenceType } from "src/services/models/merchantPreferences.js"
  import { DeliveryType } from "src/services/models/deliveryType.js"
  import Tooltip, {Wrapper, Content} from '@smui/tooltip';
  import DataTable, {Head, Body, Row, Cell} from '@smui/data-table';

  let showConfirm = false
  let initiallyLoading = true
  let inProgress = false

  let invalidValues = true

  let confirmTitle = ""
  let confirmMessage = ""

  let currentTab = 'survey-configuration';
  const tabs = [
    { value: 'survey-configuration', label: 'Survey Configuration' },
    { value: 'restrict-domains', label: 'Feedback Restrictions' }
  ];

  let domainRestrictions = [];
  let newRestriction = {
    type: 'EmailDomain',
    address: ''
  };
  let sortBy = null;
  let sortOrder = 'Ascending';
  let restrictionTypes = [
    { value: 'EmailAddress', label: 'Email' },
    { value: 'PhoneNumber',  label: 'Phone' },
    { value: 'EmailDomain',  label: 'Domain' }
  ];
  let openMenuId = null; 
  let menuPosition = { top: 0, left: 0 }; 
  let menuAnchorElement = null; 

  const restrictionHeaders = [
    { key: 'DomainType', title: 'Entry Type', sortable: true },
    { key: 'Value', title: 'Address', sortable: true },
    { key: 'actions', title: '', sortable: false, width: '75px' }
  ];

  let frequencyCount = "";
  let frequencyUnit = "";
  let delayCount = "";
  let delayUnit = "";
  let deviceLimitCount = "";
  let deviceLimitUnit = "";
  let disableSendingFeedback = false;
  let enableReceiptScanning = true;
  let feedbackRequestDeliveryPolicyPriorityOption = "";
  let smsMonthlyCapValue = "";
  let isSmsActive = false;
  let smsMonthlyCapUnlimited = false;
  let enableSurveyDelayByChannel = false; 
  let useSurveyDelayByChannel = false; 
  let originalUseSurveyDelayByChannel = false;
  let surveyChannelDelays = [];
  let originalSurveyChannelDelays = [];
  let deletedChannelIds = [];
  let availableChannels = [];
  let channelToQuestionnairesMap = {};

  let smsEstimate;
  let smsAvgPrice = "0.00";
  let smsEstimatePriceThisMonth = "0.00";
  const smsDefaultAveragePrice = 0.03;
  const smsFlatRates = 11.15;
  const emDash = "—";
  const smsMonthlyCapUnlimitedValue = -1;

  $: formData = {
    frequencyCount: frequencyCount,
    frequencyUnit: frequencyUnit,
    delayCount: delayCount,
    delayUnit: delayUnit,
    deviceLimitCount: deviceLimitCount,
    deviceLimitUnit: deviceLimitUnit,
    disableSendingFeedback: disableSendingFeedback,
    enableReceiptScanning: enableReceiptScanning,
    feedbackRequestDeliveryPolicyPriorityOption: feedbackRequestDeliveryPolicyPriorityOption,
    smsMonthlyCapValue: smsMonthlyCapValue,
    smsMonthlyCapUnlimited: smsMonthlyCapUnlimited
  };

  $: if (formData.frequencyCount === '' || formData.frequencyCount === undefined || isNaN(formData.frequencyCount) ||
      formData.delayCount === '' || formData.delayCount === undefined || isNaN(formData.delayCount) ||
      formData.deviceLimitCount === '' || formData.deviceLimitCount === undefined || isNaN(formData.deviceLimitCount)) {
        invalidValues = true
      } else {
        invalidValues = false
      };

  $: frequencyChanged = formData.frequencyCount != frequencyCount || formData.frequencyUnit != frequencyUnit
  $: delayChanged = formData.delayCount != delayCount || formData.delayUnit != delayUnit
  $: deviceLimitChanged = formData.deviceLimitCount != deviceLimitCount || formData.deviceLimitUnit != deviceLimitUnit
  $: disableSendingFeedbackChanged = formData.disableSendingFeedback != disableSendingFeedback
  $: enableReceiptScanningChanged = formData.enableReceiptScanning != enableReceiptScanning
  $: feedbackRequestDeliveryPolicyPriorityOptionChanged = formData.feedbackRequestDeliveryPolicyPriorityOption != feedbackRequestDeliveryPolicyPriorityOption
  $: smsMonthlyCapValueChanged = formData.smsMonthlyCapValue != smsMonthlyCapValue

  $: useSurveyDelayByChannelChanged = useSurveyDelayByChannel !== originalUseSurveyDelayByChannel;
  $: surveyDelayByChannelChanged = useSurveyDelayByChannel && JSON.stringify(surveyChannelDelays) !== JSON.stringify(originalSurveyChannelDelays);

  $: noChanges = !frequencyChanged && !delayChanged && !deviceLimitChanged &&
                !disableSendingFeedbackChanged && !enableReceiptScanningChanged &&
                !(isSmsActive && feedbackRequestDeliveryPolicyPriorityOptionChanged) &&
                !(isSmsActive && smsMonthlyCapValueChanged) && 
                !useSurveyDelayByChannelChanged && !surveyDelayByChannelChanged && !(deletedChannelIds.length > 0)
  $: frequencyInMinutes = convertToMinutes(formData.frequencyCount, formData.frequencyUnit)
  $: delayInMinutes = convertToMinutes(formData.delayCount, formData.delayUnit)
  $: deviceLimitInMinutes = convertToMinutes(formData.deviceLimitCount, formData.deviceLimitUnit)

  $: invalidFrequency = frequencyInMinutes < 1440 || frequencyInMinutes > 43200
  $: invalidDelay = delayInMinutes < 0 || delayInMinutes > 43200
  $: invalidDeviceLimit = deviceLimitInMinutes < 1440 || deviceLimitInMinutes > 43200
  $: invalidSmsCap = calculateInvalidSmsCap(Number(formData.smsMonthlyCapValue), formData.smsMonthlyCapUnlimited);

  function calculateInvalidSmsCap(capValue, capUnlimited) {
    if (!isSmsActive) return false;
    if (capUnlimited) return false;
    if (Number.isNaN(capValue)) return true;
    return Number(capValue) <= 0;
  }

  $: invalidChannelDelays = useSurveyDelayByChannel && surveyChannelDelays.some(channel => {
    const channelDelayInMinutes = convertToMinutes(channel.delayCount, channel.delayUnit);
    return channelDelayInMinutes < 0 || channelDelayInMinutes > 43200;
  })

  $: invalidForm = invalidFrequency || invalidDelay || invalidDeviceLimit || invalidValues || invalidSmsCap || invalidChannelDelays

  $: if (isSmsActive && smsEstimate) {
    updateSmsEstimatedPrice();
  }

  export let merchantId;

  function callSave() {
    if(frequencyChanged) {
      confirmTitle = 'Save Updated Survey Frequency'
      confirmMessage = 'Are you sure you want to update the survey frequency?'
    } else if(delayChanged) {
      confirmTitle = 'Save Updated Survey Delay'
      confirmMessage = 'Are you sure you want to update the survey delay?'
    } else if(deviceLimitChanged) {
      confirmTitle = 'Save Updated Device Limit'
      confirmMessage = 'Are you sure you want to update the device limit?'
    } else if(disableSendingFeedbackChanged) {
      if(formData.disableSendingFeedback) {
        confirmTitle = 'Disable Sending Feedback Request Emails'
        confirmMessage = 'Are you sure you want to disable sending feedback request emails?'
      } else {
        confirmTitle = 'Enable Sending Feedback Request Emails'
        confirmMessage = 'Are you sure you want to enable sending feedback request emails?'
      }
    } else if(enableReceiptScanningChanged) {
      if(formData.enableReceiptScanning) {
        confirmTitle = 'Enable Receipt Scanning'
        confirmMessage = 'Are you sure you want to enable receipt scanning?'
      } else {
        confirmTitle = 'Disable Receipt Scanning'
        confirmMessage = 'Are you sure you want to disable receipt scanning?'
      }
    } else if(feedbackRequestDeliveryPolicyPriorityOptionChanged) {
      confirmTitle = 'Save Updated Feedback Request Delivery Policy'
      confirmMessage = 'Are you sure you want to update the feedback request delivery policy?'
    } else if(smsMonthlyCapValueChanged) {
      confirmTitle = 'Save Updated SMS Monthly Cap Value'
      confirmMessage = 'Are you sure you want to update the SMS Monthly Cap?'
    } else if(useSurveyDelayByChannelChanged) {
      if(useSurveyDelayByChannel) {
        confirmTitle = 'Enable Custom Survey Delay by Channel'
        confirmMessage = 'Are you sure you want to enable custom survey delays by channel?'
      } else {
        confirmTitle = 'Disable Custom Survey Delay by Channel'
        confirmMessage = 'Are you sure you want to disable custom survey delays by channel?'
      }
    } else if(surveyDelayByChannelChanged) {
      confirmTitle = 'Save Updated Survey Delay'
      confirmMessage = 'Are you sure you want to update the survey delay?'
    }
    showConfirm = true
  }

  async function callConfirm() {
    inProgress = true
    try {
      if(invalidForm) {
        return
      }
      if(frequencyChanged) {
        await surveyApi.updateSurveyFrequency(merchantId, frequencyInMinutes)
      }
      if(delayChanged) {
        await surveyApi.updateSurveyDelay(merchantId, delayInMinutes)
      }
      if(deviceLimitChanged) {
        await surveyApi.updateDeviceLimit(merchantId, deviceLimitInMinutes)
      }
      if(disableSendingFeedbackChanged) {
        let action = 'deactivate'
        if(formData.disableSendingFeedback) {
          action = 'activate'
        }
        await surveyApi.updateSendingFeedback(merchantId, action)
      }
      if(enableReceiptScanningChanged) {
        let action = 'deactivate'
        if(formData.enableReceiptScanning) {
          action = 'activate'
        }
        await surveyApi.updateReceiptScanning(merchantId, action)
      }
      if(feedbackRequestDeliveryPolicyPriorityOptionChanged) {
        await surveyApi.updatePreferenceByKey(merchantId, PreferenceType.SURVEY_DELIVERY_TYPE, formData.feedbackRequestDeliveryPolicyPriorityOption)
      }
      if(smsMonthlyCapValueChanged) {
        const capValue = formData.smsMonthlyCapValue;
        const capUnlimited = formData.smsMonthlyCapUnlimited;
        await surveyApi.updatePreferenceByKey(merchantId, PreferenceType.SURVEY_SMS_CAP, capUnlimited ? smsMonthlyCapUnlimitedValue : capValue);
      }
      if(useSurveyDelayByChannelChanged) {
        await surveyApi.updatePreferenceByKey(merchantId, 'custom_survey_delay_by_channel', useSurveyDelayByChannel);
      }
      if(deletedChannelIds.length > 0) {
        await v3Api.deleteSurveyDelaysByChannel(merchantId, deletedChannelIds);
      }
      if(surveyDelayByChannelChanged || (useSurveyDelayByChannelChanged && useSurveyDelayByChannel)) {
        await updateSurveyDelaysByChannel();
      }
      showToastSuccess("Survey Configuration saved successfully")
      initData()
    } catch (err) {
      showToastError(err.message)
    } finally {
      inProgress = false
    }
  }

  const showToastSuccess = (toastMessage) => {
    toast.push({
      ...toastOptions,
      theme: toastSuccessTheme,
      component: {
        src: ToastSuccess,
        props: {
          message: toastMessage,
          onClose: () => {
            toast.pop();
          }
        }
      }
    });
  }

  const showToastError = (toastMessage) => {
    toast.push({
      ...toastOptions,
      theme: toastErrorTheme,
      component: {
        src: ToastError,
        props: {
          message: toastMessage || "An error occurred",
          onClose: () => {
            toast.pop();
          }
        }
      }
    });
  }

  function formatPrice(value) {
    if (!value) {
      return emDash;
    }

    return `$${Number(value).toFixed(2)}`;
  }

  function calculateEstimate(cap, transactionCountLastMonth) {
    const capValue = !Number.isNaN(cap) ? Number(cap) : smsMonthlyCapUnlimitedValue;
    if (capValue == smsMonthlyCapUnlimitedValue) {
      return emDash;
    }
    const avgPricePerSms = smsEstimate.avgPricePerSms || smsDefaultAveragePrice;
    const projectedMessageCount =(transactionCountLastMonth > 0 ? Math.min(transactionCountLastMonth, capValue) : capValue);
    return formatPrice((projectedMessageCount * avgPricePerSms) + smsFlatRates);
  }

  function updateSmsEstimatedPrice() {
    if (isSmsActive) {
      const capUnlimited = formData.smsMonthlyCapUnlimited
      formData.smsMonthlyCapValue = capUnlimited ? '' : formData.smsMonthlyCapValue

      if (capUnlimited) {
        smsEstimatePriceThisMonth = emDash;
      } else {
        const cap = formData.smsMonthlyCapValue
        const transactionCountLastMonth = smsEstimate.transactionCountLastMonth || 0;
        smsEstimatePriceThisMonth = calculateEstimate(cap, transactionCountLastMonth);
      }
    }
  }

  function calculateSmsEstimates(smsEstimate, smsMonthlyCapValue) {
    const avgPricePerSms = smsEstimate.avgPricePerSms || smsDefaultAveragePrice;
    let lastMonthActualCost = null;

    if (smsEstimate.totalPriceLastMonth !== undefined) {
      lastMonthActualCost = smsEstimate.totalPriceLastMonth;
    }
    else if (smsEstimate.transactionCountLastMonth > 0) {
      lastMonthActualCost = smsEstimate.transactionCountLastMonth * avgPricePerSms + smsFlatRates;
    }
    const formattedLastMonthCost = lastMonthActualCost !== null ? formatPrice(lastMonthActualCost) : "0.00";

    const transactionCountLastMonth = smsEstimate.transactionCountLastMonth || 0;
    const formattedProjectedMonthlyCost = calculateEstimate(smsMonthlyCapValue, transactionCountLastMonth);

    return {
      lastMonthCost: formattedLastMonthCost,
      projectedMonthlyCost: formattedProjectedMonthlyCost
    };
  }

  async function initData() {
    inProgress = true
    try {
      const getSurveyFrequencyTask = surveyApi.getSurveyFrequency(merchantId);
      const getSurveyDelayTask = surveyApi.getSurveyDelay(merchantId);
      const getDeviceLimitTask = surveyApi.getDeviceLimit(merchantId);
      const getSendingFeedbackTask = surveyApi.getSendingFeedback(merchantId);
      const getReceiptScanningTask = surveyApi.getReceiptScanning(merchantId);
      const getFeedbackRequestDeliveryPolicyPriorityOptionTask = surveyApi.getPreferenceByKey(merchantId, PreferenceType.SURVEY_DELIVERY_TYPE);
      const getSmsMonthlyCapTask = surveyApi.getPreferenceByKey(merchantId, PreferenceType.SURVEY_SMS_CAP);
      const getSurveyDelayByChannelTask = surveyApi.getPreferenceByKey(merchantId, 'custom_survey_delay_by_channel');
      const getSMSEstimateTask = smsApi.getSMSEstimate(merchantId);
      const getSMSConfigTask = smsApi.getSMSConfig(merchantId);
      const smsConfig = await getSMSConfigTask;

      isSmsActive = smsConfig.isActive;

      let { value: surveyDelayByChannel } = await getSurveyDelayByChannelTask;
      enableSurveyDelayByChannel = surveyDelayByChannel;
      useSurveyDelayByChannel = enableSurveyDelayByChannel;
      originalUseSurveyDelayByChannel = enableSurveyDelayByChannel;

      await loadRestrictedDomains();

      const merchantResponse = await merchantsApi.getMerchant(merchantId);
      const channelsResponse = await channelsApi.getChannels(merchantResponse.merchant_types_id);
      const surveyChannelDelaysResponse = await v3Api.getSurveyChannelDelays(merchantId);
      const questionnairesResponse = await questionnairesApi.getQuestionnaires(merchantId, 1000);

      availableChannels = (channelsResponse || []).filter(channel => channel.merchant_types_id === merchantResponse.merchant_types_id);
      
      const channelNameMap = {};
      if (availableChannels && Array.isArray(availableChannels)) {
        for (const channel of availableChannels) {
          if (channel && channel.id && channel.label) {
            channelNameMap[channel.id] = channel.label;
          }
        }
      }
      
      surveyChannelDelays = surveyChannelDelaysResponse.map(channel => {
        const delayObject = convertFromMinutes(channel.delayInMinutes);
        return {
          ...channel,
          delayCount: delayObject.count,
          delayUnit: delayObject.unit,
          channelName: channelNameMap[channel.channelId] || `Channel ${channel.channelId}`
        };
      });
      
      originalSurveyChannelDelays = JSON.parse(JSON.stringify(surveyChannelDelays));
      deletedChannelIds = []; 
      channelToQuestionnairesMap = {};
        
      if (questionnairesResponse && Array.isArray(questionnairesResponse)) {
        questionnairesResponse.forEach(questionnaire => {
          if (questionnaire?.channels_id) {
            const channelId = questionnaire.channels_id;
            if (!channelToQuestionnairesMap[channelId]) {
              channelToQuestionnairesMap[channelId] = [];
            }
            channelToQuestionnairesMap[channelId].push(questionnaire);
          }
        });
      }

      let { value: frequency } = await getSurveyFrequencyTask;
      let frequencyObject = convertFromMinutes(frequency)
      frequencyCount = frequencyObject.count
      frequencyUnit = frequencyObject.unit

      let { value: delay } = await getSurveyDelayTask;
      let delayObject = convertFromMinutes(delay)
      delayCount = delayObject.count
      delayUnit = delayObject.unit

      let { value: deviceLimit } = await getDeviceLimitTask;
      let deviceLimitObject = convertFromMinutes(deviceLimit)
      deviceLimitCount = deviceLimitObject.count
      deviceLimitUnit = deviceLimitObject.unit

      let { value: sendingFeedback } = await getSendingFeedbackTask;
      disableSendingFeedback = sendingFeedback

      let { value: receiptScanning } = await getReceiptScanningTask;
      enableReceiptScanning = receiptScanning

      if (isSmsActive) {
        let { value: feedbackPolicy } = await getFeedbackRequestDeliveryPolicyPriorityOptionTask;
        feedbackRequestDeliveryPolicyPriorityOption = feedbackPolicy;
        let { value: smsMonthlyCap } = await getSmsMonthlyCapTask;
        smsMonthlyCapValue = smsMonthlyCap === smsMonthlyCapUnlimitedValue ? '' : smsMonthlyCap;
        smsMonthlyCapUnlimited = smsMonthlyCap === smsMonthlyCapUnlimitedValue;
        smsEstimate = await getSMSEstimateTask;

        const smsEstimates = calculateSmsEstimates(smsEstimate, smsMonthlyCapValue);
        smsAvgPrice = smsEstimates.lastMonthCost;
        smsEstimatePriceThisMonth = smsEstimates.projectedMonthlyCost;

      }
    } catch (err) {
      showToastError(err.message)
    } finally {
      inProgress = false
    }
  }

  function convertFromMinutes(valueInMinutes) {
    let returnObject = {
      count: null,
      unit: null
    }
    if(valueInMinutes % 1440 == 0) {
      returnObject.count = valueInMinutes / 1440
      returnObject.unit = 'day'
    } else if(valueInMinutes % 60 == 0) {
      returnObject.count = valueInMinutes / 60
      returnObject.unit = 'hour'
    } else {
      returnObject.count = valueInMinutes
      returnObject.unit = 'minute'
    }
    return returnObject
  }

  function convertToMinutes(value, unit) {
    let returnValue = value
    if(unit == 'hour') {
      returnValue = value * 60
    } else if (unit == 'day') {
      returnValue = value * 60 * 24
    }
    return returnValue
  }

  function addNewSurveyChannel() {
    const configuredChannelIds = surveyChannelDelays.map(ch => ch.channelId);
    const availableChannel = availableChannels.find(channel => 
      !configuredChannelIds.includes(channel.id)
    );
    
    if (!availableChannel) {
      return;
    }
    
    const newChannel = {
      merchantId: merchantId,
      channelId: availableChannel.id,
      delayInMinutes: 90,
      delayCount: 90,
      delayUnit: 'minute',
      active: true,
      channelName: availableChannel.label
    };
    surveyChannelDelays = [...surveyChannelDelays, newChannel];
  }

  function removeChannelDelay(channelId) {
    const channelToRemove = surveyChannelDelays.find(c => c.channelId === channelId);
    
    if (channelToRemove && channelToRemove.channelId) {
      const wasOriginalChannel = originalSurveyChannelDelays.some(orig => orig.channelId === channelId);
      if (wasOriginalChannel) {
        deletedChannelIds = [...deletedChannelIds, channelId];
      }
    }
    
    surveyChannelDelays = surveyChannelDelays.filter(c => c.channelId !== channelId);
  }

  function updateChannelDelay(channelId, field, value) {
    surveyChannelDelays = surveyChannelDelays.map(channel => {
      if (channel.channelId === channelId) {
        const updatedChannel = { ...channel, [field]: value };
        
        if (field === 'delayCount' || field === 'delayUnit') {
          updatedChannel.delayInMinutes = convertToMinutes(
            field === 'delayCount' ? value : updatedChannel.delayCount,
            field === 'delayUnit' ? value : updatedChannel.delayUnit
          );
        }
        
        if (field === 'channelName') {
          const selectedChannel = availableChannels.find(c => c.label === value);
          if (selectedChannel) {
            updatedChannel.channelId = selectedChannel.id;
          }
        }
        
        return updatedChannel;
      }
      return channel;
    });
  }
  
  function getImpactedQuestionnaires(channelId) {
    return channelToQuestionnairesMap[channelId] || [];
  }

  function isChannelDelayInvalid(channel) {
    const channelDelayInMinutes = convertToMinutes(channel.delayCount, channel.delayUnit);
    return channelDelayInMinutes < 0 || channelDelayInMinutes > 43200;
  }

  async function updateSurveyDelaysByChannel() {
    const channelDelays = surveyChannelDelays.map(channel => ({
      ChannelId: channel.channelId,
      DelayInMinutes: convertToMinutes(channel.delayCount, channel.delayUnit),
      Active: channel.active
    }));

    await v3Api.updateSurveyDelaysByChannel(merchantId, channelDelays);
  }

  function mapEnumToDisplayName(enumValue) {
    const mapping = {
      'EmailDomain':  'Domain',
      'EmailAddress': 'Email',
      'PhoneNumber':  'Phone'
    };
    return mapping[enumValue] || enumValue;
  }

  async function loadRestrictedDomains() {
    try {
      const result = await v3Api.getRestrictedDomains(merchantId, sortBy, sortOrder);
      domainRestrictions = (result || []).map(restriction => ({
        ...restriction,
        displayType: mapEnumToDisplayName(restriction.domainType)
      }));
    } catch (err) {
      showToastError("Failed to load restricted domains: " + err.message);
    }
  }

  async function handleSort(columnKey) {
    if (sortBy === columnKey) {
      sortOrder = sortOrder === 'Ascending' ? 'Descending' : 'Ascending';
    } else {
      sortBy = columnKey;
      sortOrder = 'Ascending';
    }
    await loadRestrictedDomains();
  }

  async function addRestriction() {
    if (newRestriction.address.trim() && isValidRestriction) {
      try {
        const result = await v3Api.createRestrictedDomain(
          merchantId, 
          newRestriction.type, 
          newRestriction.address.trim()
        );
        if (result && result.id) {
          newRestriction.address = '';
          showToastSuccess("Restriction successfully added to list.");
          await loadRestrictedDomains();
        } else {
          showToastError("Failed to add restriction to list.");
        }
      } catch (err) {
        showToastError("Failed to add restriction to list.");
      }
    }
  }

  async function removeRestriction(restriction) {
    try {
      const result = await v3Api.deleteRestrictedDomain(merchantId, restriction.domainType, restriction.value);
      if (result.status === 200 || result.status === 204) {
        showToastSuccess("Restriction successfully removed from list.");
        await loadRestrictedDomains();
      } else {
        showToastError("Failed to remove restriction from list.");
      }
    } catch (err) {
      showToastError("Failed to remove restriction from list.");
    }
  }

  function handleRestrictionAction(action, item) {
    if (action.key === 'delete') {
      removeRestriction(item);
    }
    openMenuId = null; // Close menu after action
  }

  function toggleMenu(id, event) {
    if (openMenuId === id) {
      openMenuId = null;
      menuAnchorElement = null;
      return;
    }
    
    menuAnchorElement = event.target;
    openMenuId = id;
    
    setTimeout(() => calculateMenuPosition(), 0);
  }
  
  function calculateMenuPosition() {
    if (!menuAnchorElement) return;
    
    let menuElement = document.getElementById('restriction-flyout');
    if (!menuElement) return;
    
    let menuRect = menuElement.getBoundingClientRect();
    let anchorRect = menuAnchorElement.getBoundingClientRect();
    
    // Position below and to the left of the button
    let newTop = anchorRect.bottom + window.scrollY + 4;
    let newLeft = anchorRect.right + window.scrollX - menuRect.width;
    
    if (newLeft < 0) {
      newLeft = anchorRect.left + window.scrollX;
    }
    
    menuPosition = {
      top: newTop,
      left: newLeft
    };
  }

  function closeMenu() {
    openMenuId = null;
    menuAnchorElement = null;
  }

  function validateRestrictionAddress(address, type) {
    if (!address.trim()) return false;
    
    if (type === 'EmailAddress') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);
    } else if (type === 'PhoneNumber') {
      return /^\+?[\d\s\-\(\)]+$/.test(address);
    } else if (type === 'EmailDomain') {
      return /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/.test(address);
    }
    return true;
  }

  $: isValidRestriction = validateRestrictionAddress(newRestriction.address, newRestriction.type);

    // Store the click handler so we can remove it later
  const handleClickOutside = (event) => {
    if (!event.target.closest('.menu-container') && !event.target.closest('#restriction-flyout')) {
      closeMenu();
    }
  };

  onMount(async () => {
    await initData();
    initiallyLoading = false;
    document.addEventListener('click', handleClickOutside);
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

{#if !initiallyLoading}

  <div in:fade class="page survey-configuration-admin">
    <!-- Tab Navigation -->
    <div class="top-tab-navigation">
      <div class="custom-tabs">
        {#each tabs as tab}
          <button 
            type="button"
            class="custom-tab"
            class:active={currentTab === tab.value}
            data-cy="account-setup-tab-{tab.value}"
            on:click={() => currentTab = tab.value}
          >
            {tab.label}
          </button>
        {/each}
      </div>
    </div>

    <!-- Survey Configuration Panel -->
    {#if currentTab === 'survey-configuration'}
    <div class="panel panel-default">
      <div class="panel-heading-flex">
          <div>
            <h4 data-cy="survey-configuration-heading-title">
              <i data-cy="survey-configuration-heading-icon" class="fa fa-sliders fa-rotate-90"></i> Survey Configuration
            </h4>
            <span data-cy="survey-configuration-heading-description" class="subheading">Manage the survey configuration for the entire brand.</span>
          </div>
      </div>
      <div class="panel-body">

        <div class="panel panel-default panel-no-shadow">
          <div class="panel-heading">
              <div data-cy="survey-configuration-survey-frequency-title" class="subpanel-title">
                Survey Frequency
              </div>
              <div data-cy="survey-configuration-survey-frequency-description" class="subpanel-subheading">Manage the frequency of email and SMS surveys.</div>
          </div>
          <div class="panel-body">
            <h6 data-cy="survey-configuration-survey-frequency-instruction">Choose a minimum of 24 hours to a maximum of 30 days.</h6>
            <span data-cy="survey-configuration-survey-frequency-label" class="m-r-sm">No more than once every  </span>
            <Textfield
              type="number"
              data-cy="survey-configuration-survey-frequency-value-field"
              input$data-cy="survey-configuration-survey-frequency-value-input"
              variant="outlined"
              bind:value={formData.frequencyCount}
              class="survey-input-text m-r-md"
              input$oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              disabled={inProgress}
              invalid={invalidFrequency}
            />
            <Select data-cy="survey-configuration-survey-frequency-unit-select" bind:value={formData.frequencyUnit} variant="outlined" class="survey-input-select" disabled={inProgress}>
              <Option data-cy="survey-configuration-survey-frequency-unit-hour" value="hour">Hour(s)</Option>
              <Option data-cy="survey-configuration-survey-frequency-unit-day" value="day">Day(s)</Option>
            </Select>
            <HelperText data-cy="survey-configuration-survey-frequency-error-text" persistent slot="helper" class="helper-error">
              {#if invalidFrequency}
                Invalid value for frequency
              {/if}
            </HelperText>
          </div>
        </div>

        <div class="panel panel-default panel-no-shadow">
          <div class="panel-heading">
            <div data-cy="survey-configuration-survey-delay-title" class="subpanel-title">
              Survey Delay
            </div>
            <div data-cy="survey-configuration-survey-delay-description" class="subpanel-subheading">Manage email and SMS survey timing.</div>
          </div>
          <div class="panel-body">
            <h6 data-cy="survey-configuration-survey-delay-instruction">Choose a minimum of 0 minutes to a maximum of 30 days for ALL Channels.</h6>
            <Textfield
              type="number"
              data-cy="survey-configuration-survey-delay-value-field"
              input$data-cy="survey-configuration-survey-delay-value-input"
              variant="outlined"
              bind:value={formData.delayCount}
              class="survey-input-text m-r-md"
              input$oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              disabled={inProgress}
              invalid={invalidDelay}
            />
            <Select data-cy="survey-configuration-survey-delay-unit-select" bind:value={formData.delayUnit} variant="outlined" class="survey-input-select" disabled={inProgress}>
                <Option data-cy="survey-configuration-survey-delay-unit-minute" value="minute">Minute(s)</Option>
                <Option data-cy="survey-configuration-survey-delay-unit-hour" value="hour">Hour(s)</Option>
                <Option data-cy="survey-configuration-survey-delay-unit-day" value="day">Day(s)</Option>
            </Select>
            {#if invalidDelay}
            <HelperText data-cy="survey-configuration-survey-delay-error-text" persistent slot="helper" class="helper-error">
              Invalid value for delay
            </HelperText>
            {/if}
            
            <div class="survey-configuration-checkbox-wrapper" style="margin-top: 20px;">
              <FormField>
                <Checkbox
                  data-cy="survey-configuration-survey-delay-customize-by-channel-checkbox"
                  bind:checked={useSurveyDelayByChannel}
                />
                <span data-cy="survey-configuration-survey-delay-customize-by-channel-label" slot="label">Customize Survey Delay by Channel</span>
              </FormField>
            </div>
              <div class="channel-delay-customization" class:disabled={!useSurveyDelayByChannel}>
                <div class="channel-delay-info">
                  <small>
                    Custom Survey Delays will override the delay set above.
                  </small>
                </div>
                
                {#each surveyChannelDelays as channel (channel.channelId)}
                  <div class="channel-delay-row">
                    <div class="channel-delay-controls">
                      <Select 
                        bind:value={channel.channelName} 
                        variant="outlined" 
                        class="survey-input-select dropdown-priority-options m-r-md"
                        on:change={(e) => updateChannelDelay(channel.channelId, 'channelName', e.detail.value)}
                        disabled={inProgress || !useSurveyDelayByChannel}
                      >
                        {#each availableChannels as availableChannel}
                          <Option value={availableChannel.label}>{availableChannel.label}</Option>
                        {/each}
                      </Select>
                      
                      <Textfield
                        type="number"
                        variant="outlined"
                        bind:value={channel.delayCount}
                        class="survey-input-text m-r-md"
                        input$oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                        on:input={(e) => updateChannelDelay(channel.channelId, 'delayCount', e.target.value)}
                        disabled={inProgress || !useSurveyDelayByChannel}
                        invalid={isChannelDelayInvalid(channel)}
                      />
                      <Select 
                        bind:value={channel.delayUnit} 
                        variant="outlined" 
                        class="survey-input-select m-r-md"
                        on:change={(e) => updateChannelDelay(channel.channelId, 'delayUnit', e.detail.value)}
                        disabled={inProgress || !useSurveyDelayByChannel}
                      >
                        <Option value="minute">Minute(s)</Option>
                        <Option value="hour">Hour(s)</Option>
                        <Option value="day">Day(s)</Option>
                      </Select>
                      
                      <button 
                        type="button" 
                        class="btn btn-sm m-r-md"
                        on:click={() => removeChannelDelay(channel.channelId)}
                        disabled={inProgress || !useSurveyDelayByChannel}
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                      
                      <div class="questionnaires-impacted">
                        {#if getImpactedQuestionnaires(channel.channelId).length > 0}
                          <Wrapper rich>
                            <small class="questionnaires-link">
                              <i class="fa fa-info-circle"></i>
                              {getImpactedQuestionnaires(channel.channelId).length} Questionnaire{getImpactedQuestionnaires(channel.channelId).length !== 1 ? 's' : ''} Impacted
                            </small>
                            <Tooltip>
                              <Content>
                                <div class="questionnaires-tooltip">
                                  <ul class="questionnaires-list">
                                    {#each getImpactedQuestionnaires(channel.channelId) as questionnaire}
                                      <li class="questionnaire-item">{questionnaire.title || `Questionnaire ${questionnaire.id}`}</li>
                                    {/each}
                                  </ul>
                                </div>
                              </Content>
                            </Tooltip>
                          </Wrapper>
                        {:else}
                          <small class="no-questionnaires">
                            <i class="fa fa-info-circle"></i>
                            No Questionnaires Impacted
                          </small>
                        {/if}
                      </div>
                    </div>
                    
                    {#if isChannelDelayInvalid(channel)}
                      <div class="channel-delay-error">
                        <HelperText persistent slot="helper" class="helper-error">
                          Invalid value for delay
                        </HelperText>
                      </div>
                    {/if}
                  </div>
                {/each} 
                
                <div class="add-channel-section">
                  <Button
                    type="raised"
                    on:click={addNewSurveyChannel}
                    disabled={inProgress || !useSurveyDelayByChannel || surveyChannelDelays.length >= availableChannels.length}
                  >
                    <Label>
                      <i class="fa fa-plus m-r-sm"></i>
                      Add New Survey Channel
                    </Label>
                  </Button>
                </div>
              </div>
          </div>
        </div>

        {#if isSmsActive}
          <div class="panel panel-default panel-no-shadow">
            <div class="panel-heading">
              <div data-cy="survey-configuration-feedback-request-delivery-policy-title" class="subpanel-title">
                Feedback Request Delivery Policy
              </div>
              <div data-cy="survey-configuration-feedback-request-delivery-policy-description" class="subpanel-subheading">
                Manage the delivery of feedback to your guests. To review SMS activity, go to the <a class="sms-config-link" href="/#/admin/global/sms">SMS Surveys page</a>.
              </div>
            </div>
            <div class="panel-body">
              <h6 data-cy="survey-configuration-feedback-request-delivery-policy-instruction">Select a Priority</h6>
              <Select
                data-cy="survey-configuration-feedback-request-delivery-policy-select"
                bind:value={formData.feedbackRequestDeliveryPolicyPriorityOption}
                variant="outlined"
                class="survey-input-select dropdown-priority-options"
                disabled={inProgress}
              >
                <Option data-cy="survey-configuration-feedback-request-delivery-policy-option-email-first" value={DeliveryType.EMAIL_THEN_SMS}>Email, then SMS</Option>
                <Option data-cy="survey-configuration-feedback-request-delivery-policy-option-sms-first" value={DeliveryType.SMS_THEN_EMAIL}>SMS, then Email</Option>
              </Select>

              <h6 class="sms-cap-title">Manage a maximum cap for text messages monthly</h6>
              <h6 class="sms-cap-subtitle">Choose the max. Value must be greater than 1.</h6>
              <div class="sms-cap-wrapper" style="display:inline-block">
                <Textfield
                  type="number"
                  min="1"
                  variant="outlined"
                  bind:value={formData.smsMonthlyCapValue}
                  class="survey-input-text m-r-md sms-cap-field"
                  input$oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                  on:input={updateSmsEstimatedPrice}
                  disabled={inProgress || formData.smsMonthlyCapUnlimited}
                  invalid={invalidSmsCap}
                />
              </div>
              <div class="survey-configuration-checkbox-wrapper" style="display:inline-block">
                <FormField>
                  <Checkbox
                    data-cy="survey-configuration-feedback-delivery-policy-unlimited-cap-checkbox"
                    bind:checked={formData.smsMonthlyCapUnlimited}
                    on:change={updateSmsEstimatedPrice}
                  />
                <span data-cy="survey-configuration-feedback-delivery-policy-unlimited-cap-label" slot="label">No Message Cap</span>
              </FormField>
              </div>
              <div class="sms-monthly-estimate-section">
                <h6 class="sms-monthly-estimate-description">For reference, here is the spend for last month, and approximate spend for this month based on your cap.</h6>
                <div class="sms-monthly-estimate-description">
                  <div class="sms-monthly-estimate-fields">
                    <Wrapper rich>
                      <h6 class="field-title">
                        Last Month's Spend
                        <small><i class="fa fa-question-circle tooltip-icon"></i></small>
                        <Tooltip>
                          <Content>
                            <p class="tooltip-content">
                              If SMS was not enabled for last month, this will be an estimation based on costs and transactions from the prior month. If SMS has been enabled, this is what you spent based on last month's history. If there are no transactions for last month, the value will be $0.
                            </p>
                          </Content>
                        </Tooltip>
                      </h6>
                      </Wrapper>
                    <div class="field-content">
                      {smsAvgPrice}
                    </div>
                  </div>
                  <div class="sms-monthly-estimate-fields">
                    <Wrapper rich>
                      <h6 class="field-title">
                        Est. of This Month's Spend
                        <small><i class="fa fa-question-circle tooltip-icon"></i></small>
                        <Tooltip>
                          <Content>
                            <p class="tooltip-content">
                              This month's spend is calculated using the average cost per text in the U.S., though fees may vary.
                            </p>
                          </Content>
                        </Tooltip>
                      </h6>
                      </Wrapper>
                    <div class="field-content">
                      {smsEstimatePriceThisMonth}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <div class="panel panel-default panel-no-shadow">
          <div class="panel-heading">
            <div data-cy="survey-configuration-device-limit-title" class="subpanel-title">
              Device Limit
            </div>
            <div data-cy="survey-configuration-device-limit-description" class="subpanel-subheading">Manage how frequently a single device can submit a survey. However, this can be overridden at the survey level.</div>
          </div>
          <div class="panel-body">
            <h6 data-cy="survey-configuration-device-limit-instruction">Choose a minimum of 24 hours to a maximum of 30 days.</h6>
            <span data-cy="survey-configuration-device-limit-label" class="m-r-sm">No more than once every  </span>
            <Textfield
              type="number"
              data-cy="survey-configuration-device-limit-value-field"
              input$data-cy="survey-configuration-device-limit-value-input"
              variant="outlined"
              bind:value={formData.deviceLimitCount}
              class="survey-input-text m-r-md"
              input$oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              disabled={inProgress}
              invalid={invalidDeviceLimit}
            />
            <Select data-cy="survey-configuration-device-limit-unit-select" bind:value={formData.deviceLimitUnit} variant="outlined" class="survey-input-select" disabled={inProgress}>
              <Option data-cy="survey-configuration-device-limit-unit-hour" value="hour">Hour(s)</Option>
              <Option data-cy="survey-configuration-device-limit-unit-day" value="day">Day(s)</Option>
            </Select>
            <HelperText data-cy="survey-configuration-device-limit-error-text" persistent slot="helper" class="helper-error">
              {#if invalidDeviceLimit}
                Invalid value for device limit
              {/if}
            </HelperText>
          </div>
        </div>

        <div class="panel panel-default panel-no-shadow">
          <div class="panel-heading">
            <div data-cy="survey-configuration-feedback-requests-title" class="subpanel-title">
              Feedback Requests
            </div>
            <div data-cy="survey-configuration-feedback-requests-description" class="subpanel-subheading">Manage the dispatch of customer feedback requests. Enable or disable email communications based on your engagement strategy.</div>
          </div>
          <div class="panel-body">
            <div class="survey-configuration-checkbox-wrapper">
              <FormField>
                <Checkbox
                  data-cy="survey-configuration-feedback-requests-disable-checkbox"
                  bind:checked={formData.disableSendingFeedback}
                />
                <span data-cy="survey-configuration-feedback-requests-label" slot="label">Disable sending customer feedback request emails</span>
              </FormField>
            </div>
          </div>
        </div>

        <div class="panel panel-default panel-no-shadow">
          <div class="panel-heading">
            <div data-cy="survey-configuration-receipt-scanning-title" class="subpanel-title">
              Receipt Scanning
            </div>
            <div data-cy="survey-configuration-receipt-scanning-description" class="subpanel-subheading">Manage the feature allowing customers to upload receipt photos for the collection of item-level feedback when a partner order is not available.</div>
          </div>
          <div class="panel-body">
            <div class="survey-configuration-checkbox-wrapper">
              <FormField>
                <Checkbox
                  data-cy="survey-configuration-receipt-scanning-enable-checkbox"
                  bind:checked={formData.enableReceiptScanning}
                />
                <span data-cy="survey-configuration-receipt-scanning-label" slot="label">Enable Receipt Scanning for Item-Level Feedback</span>
              </FormField>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="pull-right">
          <Button
            autoId="survey-configuration-save-button"
            type="raised"
            disabled={inProgress || noChanges || invalidForm}
            on:click={callSave}
          >
            <Label>
              <div class="label-contents">
                {#if inProgress}
                  <CircularProgress style="height: 16px; width: 16px; margin-right: 5px;" indeterminate/>
                {/if}
                <div>Save</div>
              </div>
            </Label>
          </Button>
        </div>
      </div>
    </div>
    {/if}

    <!-- Restrict Domains Panel -->
    {#if currentTab === 'restrict-domains'}
    <div class="panel panel-default">
      <div class="panel-heading-flex">
          <div>
            <h4 data-cy="restrict-domains-heading-title">
              <i data-cy="restrict-domains-heading-icon" class="fa fa-shield"></i> Feedback Restrictions
            </h4>
            <span data-cy="restrict-domains-heading-description" class="subheading">Users associated with emails, domains, or phone numbers on this list will not receive feedback requests.</span>
          </div>
      </div>
      <div class="panel-body">
        <div class="panel panel-default panel-no-shadow">
          <div class="panel-heading">
            <div class="subpanel-title">
              Manage Restrictions
            </div>
            <div class="subpanel-subheading">Manage emails, domains, or phone numbers you'd like to restrict from feedback requests.</div>
          </div>
          <div class="panel-body">
            <div class="restriction-form">
              <div class="form-row">
                <div class="form-group">
                  <span class="form-label">Select Entry Type</span>
                  <Select bind:value={newRestriction.type} variant="outlined" class="survey-input-select restriction-select">
                    {#each restrictionTypes as type}
                      <Option value={type.value}>{type.label}</Option>
                    {/each}
                  </Select>
                </div>
                <div class="form-group">
                  <span class="form-label">Enter Address</span>
                  <Textfield
                    bind:value={newRestriction.address}
                    variant="outlined"
                    class="restriction-input"
                    placeholder={newRestriction.type === 'EmailAddress' ? 'example@domain.com' : 
                                newRestriction.type === 'PhoneNumber' ? '(234) 567-8901' : 'example.com'}
                    invalid={newRestriction.address && !isValidRestriction}
                  />
                </div>
                <div class="form-group">
                  <span class="form-label">&nbsp;</span>
                  <Button
                    type="raised"
                    disabled={!newRestriction.address.trim() || !isValidRestriction}
                    on:click={addRestriction}
                    class="add-button"
                  >
                    <Label>Add</Label>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Restrictions Table -->
    <div class="restrictions-table-container">
      {#if domainRestrictions.length > 0}
        <div class="wasabi-table-wrapper">
          <DataTable class="wasabi-data-table" data-cy="domain-restrictions-table">
            <Head>
              <Row>
                {#each restrictionHeaders as header}
                  <Cell 
                    class="wasabi-table-header {header.sortable ? 'sortable' : ''}"
                    data-cy="domain-restrictions-table-header-{header.key}"
                    style={header.width ? `width: ${header.width}` : ''}
                    on:click={header.sortable ? () => handleSort(header.key) : undefined}
                  >
                    <div class="header-content">
                      <span>{header.title}</span>
                      {#if header.sortable && sortBy === header.key}
                        <i class="fa fa-sort-{sortOrder === 'Ascending' ? 'up' : 'down'} sort-icon"></i>
                      {:else if header.sortable}
                        <i class="fa fa-sort sort-icon inactive"></i>
                      {/if}
                    </div>
                  </Cell>
                {/each}
              </Row>
            </Head>
            <Body>
              {#each domainRestrictions as restriction (restriction.id)}
                <Row data-cy="domain-restrictions-table-{restriction.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}-row">
                  <Cell 
                    class="wasabi-table-cell entry-type-cell"
                    data-cy="domain-restrictions-table-{restriction.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}-type"
                  >
                    {restriction.displayType}
                  </Cell>
                  <Cell 
                    class="wasabi-table-cell address-cell"
                    data-cy="domain-restrictions-table-{restriction.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}-address"
                  >
                    {restriction.value}
                  </Cell>
                  <Cell class="wasabi-table-cell actions-cell">
                    <div class="actions-container">
                      <div class="menu-container">
                        <button 
                          class="action-menu-button"
                          on:click={(e) => toggleMenu(restriction.id, e)}
                          data-cy="domain-restrictions-table-{restriction.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}-menu-button"
                        >
                          <i class="fa fa-ellipsis-v"></i>
                        </button>
                      </div>
                    </div>
                  </Cell>
                </Row>
              {/each}
            </Body>
          </DataTable>
        </div>
      {/if}
    </div>
    {/if}

    <!-- Restriction Flyout Tooltip -->
    {#if openMenuId !== null}
      <div 
        id="restriction-flyout"
        class="restriction-flyout"
        style="top: {menuPosition.top}px; left: {menuPosition.left}px;"
      >

        <button 
          class="flyout-button flyout-button-secondary"
          on:click={() => handleRestrictionAction({key: 'delete'}, domainRestrictions.find(r => r.id === openMenuId))}
        >
          Remove Entry
        </button>
      </div>
    {/if}

  </div>
{:else}
  <div class="loading-container">
    <CircularProgress style="height: 40px; width: 40px;" indeterminate />
    <p>Loading configuration...</p>
  </div>
{/if}

<div class="toast-wrapper"><SvelteToast /></div>

<ConfirmSurveyConfigurationModal bind:show={showConfirm} title={confirmTitle} message={confirmMessage} on:actionConfirm={() => callConfirm()}/>

<style>
.panel-footer {
  contain: content;
}

* :global(button) {
  height: 30px;
  font-size: 14px;
  font-weight: normal;
}

.label-contents {
  display: flex;
  align-items: center;
}

:global(.strong) {
  font-weight: bold;
}

:global(.panel-no-shadow) {
  box-shadow: none;
}

:global(.survey-configuration-admin .subpanel-title) {
  font-size: 14px;
  font-weight: 500;
}

:global(.survey-configuration-admin .subpanel-subheading) {
  font-size: 12px;
}

:global(.survey-configuration-admin .survey-input-select .mdc-select__anchor) {
  height: 40px;
  width: 130px;
}

:global(.survey-configuration-admin .survey-input-text) {
  height: 40px;
  width: 80px;
  margin-bottom: 0;
}

:global(.survey-configuration-admin .survey-input-select .mdc-select__selected-text),
:global(.survey-configuration-admin .survey-input-select .mdc-deprecated-list) {
  font-size: 14px;
}

:global(.survey-configuration-admin .survey-configuration-checkbox-wrapper label) {
  line-height: 35px;
  font-size: 14px;
  font-weight: normal;
}

:global(.survey-configuration-admin .survey-configuration-checkbox-wrapper .mdc-checkbox) {
  margin-bottom: 6px;
}

:global(.field-content) {
  font-size: 12pt;
  display: flex;
  justify-content: center;
  padding: 0 10px 0 0px;
}

:global(.dropdown-priority-options){
  width: 200px;
}

:global(.sms-cap-title){
  margin: 20px 0 0 3px;
}

:global(.sms-cap-wrapper){
  margin-left: 3px;
}

:global(.sms-cap-subtitle){
  margin-top: 20px;
  margin-left: 3px;
}

:global(.sms-cap-field){
  width: 95px !important;
}

:global(.sms-monthly-estimate-section){
  margin-top: 20px;
}

:global(.sms-monthly-estimate-description){
  margin: 0 8px 8px 5px;
}

:global(.sms-monthly-estimate-fields){
  display: inline-block;
  margin:0 30px;
}

:global(.field-title){
  display:block;
  font-size: 12.5px;
  margin-top: 0;
}

:global(.sms-config-link){
  color: #005cc5;
}

:global(.tooltip-content){
  font-size: 12px;
  margin: 0 !important;
  line-height: 1.6;
}

:global(.mdc-tooltip--rich .mdc-tooltip__surface){
  transform: translateX(-50%) !important;
  display: block !important;
  overflow: unset;
}

:global(.mdc-tooltip__surface .mdc-tooltip__content::before){
  display: flex !important;
  height: 100% !important;
}

:global(.tooltip-icon){
  color: #222f48;
  font-size: 12px;
}
:global(.tooltip-icon:hover){
  color: #005cc5;
}

:global(.mdc-tooltip__surface){
  height: auto !important;
  padding: 0 !important;
  min-width: 200px !important;
  max-width: 350px !important;
}

:global(.mdc-tooltip--rich .mdc-tooltip__content) {
  margin: 0 !important;
}

:global(.loading-container) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80vh;
  color: #333;
  font-size: 14px;
}

:global(.channel-delay-row) {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}

:global(.channel-delay-controls) {
  display: flex;
  align-items: center;
}

:global(.questionnaires-impacted) {
  display: flex;
  align-items: center;
}

:global(.questionnaires-tooltip) {
  padding: 12px 16px;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
}

:global(.questionnaires-list) {
  margin: 8px 0 0 0;
  padding-left: 16px;
  list-style-type: disc;
  max-height: 250px;
  overflow-y: auto;
}

:global(.questionnaire-item) {
  padding: 3px 0;
  font-size: 12px;
  line-height: 1.4;
}

:global(.questionnaires-link) {
  color: #353535;
  cursor: pointer;
}

:global(.questionnaires-link:hover) {
  color: #0577d1;
}

:global(.no-questionnaires) {
  color: #ABABAB;
}

:global(.channel-delay-customization.disabled) {
  pointer-events: none;
}

:global(.channel-delay-customization.disabled .channel-delay-info),
:global(.channel-delay-customization.disabled .questionnaires-link),
:global(.channel-delay-customization.disabled .no-questionnaires) {
  color: #ABABAB !important;
}

:global(.channel-delay-row .mdc-select__menu .mdc-deprecated-list) {
  max-height: 250px !important;
  overflow-y: auto !important;
}

:global(.channel-delay-error) {
  margin-top: 8px;
  margin-bottom: 0;
  margin-left: 0;
}

/* Top Tab Navigation Styling */
.top-tab-navigation {
  margin-bottom: 20px;
  padding: 0;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid #e0e0e0;
}

.custom-tabs {
  display: flex;
  justify-content: center;
  gap: 0;
}

.custom-tab {
  min-width: 190px;
  height: 36px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 400;
  text-transform: none;
  color: #333;
  background: transparent;
  border: none;
  border-bottom: 1px solid transparent;
  cursor: pointer;
  position: relative;
}

.custom-tab.active {
  font-weight: 700;
  color: #005cc5;
  border-bottom: 4px solid #005cc5;
}

.custom-tab:hover {
  color: #1a365d;
  background: #e6f3ff;
  border-bottom: 4px solid #1a365d;
}

.custom-tab:focus {
  color: #2b77ad;
  background: white;
  outline: 2px solid #b3d9ff;
  font-weight: 500;
}

/* Domain Restrictions Styling */
.form-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.restriction-form .form-group {
  margin-bottom: 0;
  padding-bottom: 0;
}


.form-label {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #353535;
}

:global(.restriction-select .mdc-select__anchor) {
  width: 150px;
  height: 40px;
}

:global(.restriction-input) {
  width: 350px;
  height: 40px !important;
  margin-top: 0 !important;
}

:global(.add-button) {
  height: 40px;
  background-color: #0d7df2;
  color: white;
}

/* Restrictions Table Styling (outside panel, on background) */
.restrictions-table-container {
  margin-top: 20px;
  padding: 0;
}

.wasabi-table-wrapper {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 0;
  overflow: hidden;
}

:global(.wasabi-data-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

:global(.wasabi-table-header) {
  background-color: rgba(224, 224, 224, 0.75) !important;
  color: #333 !important;
  height: 50px !important;
  padding: 0 10px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  position: relative;
  opacity: 1 !important;
}

:global(.wasabi-table-header.sortable) {
  cursor: pointer;
  user-select: none;
}

:global(.wasabi-table-header.sortable:hover) {
  background-color: rgba(224, 224, 224, 0.9) !important;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.sort-icon {
  font-size: 12px;
  margin-left: 4px;
}

.sort-icon.inactive {
  opacity: 0.3;
}

:global(.wasabi-table-cell) {
  padding: 0 10px !important;
  border: none !important;
  height: 50px;
  vertical-align: middle;
  white-space: nowrap;
}

:global(.wasabi-data-table tbody tr:nth-of-type(even)) {
  background-color: rgba(224, 224, 224, 0.35) !important;
}

:global(.wasabi-data-table tbody tr:hover) {
  background-color: rgba(224, 224, 224, 0.5) !important;
}

.entry-type-cell {
  font-weight: 500;
  color: #666;
}

.address-cell {
  font-family: monospace;
  color: #333;
}

.actions-cell {
  width: 75px;
  text-align: right;
}

.actions-container {
  display: flex;
  justify-content: flex-end;
}

.menu-container {
  position: relative;
}

.action-menu-button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #666;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-menu-button:hover {
  background: #f0f0f0;
  color: #333;
}

.action-menu-button i {
  font-size: 14px;
}

.restriction-flyout {
  position: fixed;
  padding: 0;
  z-index: 9999;
  color: #353535;
  background-color: #ffffff;
  border: 1px solid #BEBEBE;
  border-radius: 5px;
  overflow: hidden;
}

.flyout-button {
  display: block;
  width: 100%;
  height: 100%;
  padding-left: 12px;
  padding-right: 46px;
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  transition: all 0.2s ease;
  color: #616c7f;
  background: transparent;
}

.flyout-button:hover {
  color: #333;
  background: #f5f5f5;
}

</style>
